import { z } from "zod";

/**
 * Stripe Connect onboarding for an organization's own payment account.
 * Donations are direct charges on the org's connected account (the org is the
 * merchant of record); CauseTide takes no platform fee.
 */

/** countries offered in the connect form; Stripe validates the final value */
export const STRIPE_CONNECT_COUNTRIES = [
  { code: "US", name: "United States" },
  { code: "CA", name: "Canada" },
  { code: "GB", name: "United Kingdom" },
  { code: "IE", name: "Ireland" },
  { code: "AU", name: "Australia" },
  { code: "NZ", name: "New Zealand" },
  { code: "DE", name: "Germany" },
  { code: "FR", name: "France" },
  { code: "NL", name: "Netherlands" },
  { code: "ES", name: "Spain" },
  { code: "IT", name: "Italy" },
] as const;

/** sensible default country for an org's base currency (null = ask) */
export function defaultConnectCountry(currency: string): string | null {
  return ({ USD: "US", CAD: "CA", GBP: "GB", AUD: "AU", NZD: "NZ" } as Record<string, string>)[currency.toUpperCase()] ?? null;
}

export const zStripeConnectRequest = z.object({
  /** ISO 3166-1 alpha-2; only used when the account is first created (Stripe can't change it later) */
  country: z.string().regex(/^[A-Z]{2}$/).optional(),
});
export type StripeConnectRequest = z.infer<typeof zStripeConnectRequest>;

export interface StripeConnectStatus {
  /** STRIPE_SECRET_KEY is set on this server */
  configured: boolean;
  /** webhook signing secret is set (without it gifts can't be confirmed, so checkout is refused) */
  webhooksConfigured: boolean;
  /** sandbox orgs never take payments */
  sandbox: boolean;
  /** "connect" = onboarded in-app; "manual" = legacy STRIPE_ACCOUNT_MAP entry; null = none */
  source: "connect" | "manual" | null;
  accountId: string | null;
  chargesEnabled: boolean;
  payoutsEnabled: boolean;
  detailsSubmitted: boolean;
  syncedAt: string | null;
  /** the public giving page can take real payments right now */
  acceptingPayments: boolean;
  /** suggested country for a new account, from the org's base currency (null = ask) */
  defaultCountry: string | null;
}
